import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';
import { IStorageUnit } from '../storageUnit/storageUnit';
import { ArrayHelper } from '@zz2/zz2-ui';
import { IStorageLocation } from './storageLocation';
import { IStorageLocationUpsert } from './storageLocationUpsert';
import { IStorageLocationFormValues } from './storageLocationFormValues';
import { IStorageUnitLocationUpsert } from '../storageUnitLocation/storageUnitLocationUpsert';

export default class StorageLocationModelHelper {
    public static createFormValues = (storageLocation : IStorageLocation | null, storageUnits : Array<IStorageUnit>) : IStorageLocationFormValues => {
        if (!storageLocation) return {
            id: 0,
            guid: uuidV4(),
            code: '',
            description: '',
            storageUnits: [],
            isActive: true,
        };

        const storageUnitIds = storageLocation.storageUnitLocations.filter(x => x.isActive).map(bridge => bridge.storageUnitId);
        const storageUnitOptions = storageUnits.filter(x => storageUnitIds.includes(x.id))
            .map((x) => {
                return { label: `(${x.code}) - ${x.description}`, value: x.id };
            }) ?? [];

        return {
            id: storageLocation.id,
            guid: storageLocation.guid,
            code: storageLocation.code,
            description: storageLocation.description,
            storageUnits: storageUnitOptions,
            isActive: storageLocation.isActive,
        };
    };

    public static createUpsert = (formValues : IStorageLocationFormValues, storageLocation : IStorageLocation | null) : IStorageLocationUpsert => {
        const existingBridges = storageLocation?.storageUnitLocations ?? [];

        const updatedStorageUnitIds = ArrayHelper
            .uniqueJoin(formValues.storageUnits.map(x => Number(x.value)), existingBridges.map(x => x.storageUnitId), a => a);

        const upsertBridges : Array<IStorageUnitLocationUpsert> = updatedStorageUnitIds.map((unitId) =>  {
            const shouldBeActiveBridge = formValues.storageUnits.find(x => Number(x.value) === unitId);
            const existingBridge = existingBridges.find(x => x.storageUnitId === unitId);

            return {
                id: existingBridge?.id ?? 0,
                guid: existingBridge?.guid ?? uuidV4(),
                storageUnitId: unitId,
                storageLocationId: storageLocation?.id ?? 0,
                isActive: !!shouldBeActiveBridge,
            };
        });

        return {
            id: formValues.id,
            guid: formValues.guid,
            code: formValues.code,
            description: formValues.description,
            storageUnitLocationUpsertList: upsertBridges,
            isActive: formValues.isActive,
        };
    };

    public static formSchema = () : Yup.ObjectSchema<IStorageLocationFormValues> => Yup.object().shape({
        id: Yup.number().typeError('Please enter a number.').required('Required'),
        guid: Yup.string().required('Required'),
        code: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        description: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        storageUnits: Yup.array().nullable().defined().required('Required'),
        isActive: Yup.boolean().required('Required'),
    });
}
